<template>
	<div class="autosuggest" v-show="optionsLocal.length">
		<ul
			v-if="!isGroupItems && options.length"
			class="autosuggest__list"
			data-test="notGroupList"
		>
			<li
				@click="
					$emit('selected', {
						value: item.value,
						text: item.text,
						textValue: item.textValue,
						groupName: null
					})
				"
				@keydown.enter="
					$emit('selected', {
						value: item.value,
						text: item.text,
						textValue: item.textValue,
						groupName: null
					})
				"
				class="autosuggest__list-item"
				v-for="(item, index) in optionsLocal"
				:id="index"
				:key="index"
				:tabindex="0"
			>
				<div
					v-html="item.text"
					class="autosuggest__list-item-text"
					tabindex="-1"
					data-test="itemText"
				/>
				<div v-if="item.subText" class="autosuggest__list-item-subtext">
					{{ item.subText }}
				</div>
			</li>
		</ul>

		<div v-else class="autosuggest__list-group">
			<dl
				class="autosuggest__list-group-list"
				v-for="(item, index) in optionsLocal"
				:key="index"
				data-test="groupList"
			>
				<dt
					v-if="item.items.length && !hideGroupsLabel"
					class="autosuggest__list-group-title"
				>
					<span class="autosuggest__list-group-name">
						{{ item.groupName }}
					</span>
					<!-- optional display value -->
					<span
						v-if="item.totalCount"
						class="autosuggest__list-group-counter"
					>
						{{ `(${item.items.length} of ${item.totalCount})` }}
					</span>
				</dt>
				<dd
					class="autosuggest__list-group-item"
					v-for="(option, optionIndex) in item.items"
					:key="optionIndex"
					@click="
						$emit('selected', {
							value: option.value,
							text: option.text,
							textValue: option.textValue,
							groupName: item.groupName
						})
					"
					@keydown.enter="
						$emit('selected', {
							value: option.value,
							text: option.text,
							textValue: option.textValue,
							groupName: item.groupName
						})
					"
				>
					<div
						class="autosuggest__list-group-item-text"
						v-html="option.text"
						tabindex="-1"
						data-test="itemTextGroup"
					/>
					<div
						v-if="option.subText"
						class="autosuggest__list-group-item-subtext"
					>
						{{ option.subText }}
					</div>
				</dd>

				<div
					v-show="item.items.length === 0"
					class="autosuggest__list-group-no-results"
				>
					<dt class="autosuggest__list-group-title">
						<div class="autosuggest__list-group-name">
							{{ item.groupName }}
						</div>
					</dt>
					<dd class="autosuggest__list-group-item">
						No results were found
					</dd>
				</div>
			</dl>
		</div>
	</div>
</template>

<script>
//libraries
import _ from 'lodash';
import { createTextValueObj } from '@/utils/object';

export default {
	name: 'Autosuggest',
	props: {
		searchQuery: [String, Number],
		options: {
			type: Array,
			default: () => [],
			validator: options => {
				const withOptions = options.every(option => {
					return (
						typeof option === 'object' &&
						(('text' in option && 'value' in option) ||
							('groupName' in option &&
								'items' in option &&
								'totalCount' in option))
					);
				});
				return withOptions || options.length === 0;
			}
		},
		counter: { type: Boolean, default: true },
		hideGroupsLabel: Boolean
	},
	data() {
		return {
			optionsLocal: []
		};
	},
	created() {
		this.createLocalOptions();
		this.optionsLocal = this.filterOccurrences(
			this.options,
			this.searchQuery
		);
	},
	computed: {
		isGroupItems() {
			return this.optionsLocal.every(
				option => 'groupName' in option && 'items' in option
			);
		}
	},
	methods: {
		createLocalOptions() {
			this.optionsLocal = _.cloneDeep(this.options);
		},
		previousItem() {
			// to implement
		},
		nextItem() {
			// to implement
		},
		filterOccurrences(array, searchValue) {
			if (!searchValue) {
				return _.cloneDeep(array);
			}

			const result = [];

			if (!this.isGroupItems) {
				for (let i = 0; i < array.length; i++) {
					result.push({
						text: createTextValueObj(array[i].text, searchValue),
						textValue: array[i].textValue,
						subText: array[i].subText,
						value: array[i].value
					});
				}
			} else {
				for (let i = 0; i < array.length; i++) {
					result.push({
						groupName: array[i].groupName,
						items: [],
						totalCount: array[i].totalCount
					});

					for (let j = 0; j < array[i].items.length; j++) {
						result[i].items.push({
							text: createTextValueObj(
								array[i].items[j].text,
								searchValue
							),
							textValue: array[i].items[j].textValue,
							subText: array[i].items[j].subText,
							value: array[i].items[j].value
						});
					}
				}
			}

			return result;
		}
	},
	watch: {
		options(newValue) {
			this.createLocalOptions(newValue);

			if (this.searchQuery) {
				this.optionsLocal = this.filterOccurrences(
					this.optionsLocal,
					this.searchQuery
				);
			}
		},
		searchQuery(newValue) {
			this.createLocalOptions(this.options);

			if (newValue) {
				this.optionsLocal = this.filterOccurrences(
					this.optionsLocal,
					newValue
				);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';

.autosuggest {
	width: max-content;
	min-width: 100%;
	@include stylized-dropdown;
	z-index: 999;

	&__list {
		font-size: $bd-txt-font-size-regular;
		max-height: 200px; // display scroll if there's more than 10 items
		overflow-y: auto;
		@include scrollbar;
		padding: $padding-sm 0;
	}

	&__list-item,
	&__list-group-item {
		cursor: pointer;
		position: relative;
		padding: $padding-xs $padding-sm;

		&:hover {
			background: lighten($neutral-4, 5%);
			@include text-contrast(lighten($neutral-4, 5%), $white, $black);
		}

		&:focus::after {
			content: '';
			@include vertical-horizontal-center-with-transform;
			background-color: none;
			display: block;
			width: 100%;
			height: 100%;
			border: $form-input-border--focus;
		}

		&:focus-visible {
			outline: none;
		}

		&.disabled {
			pointer-events: none;
			opacity: $disabled-opacity;
		}
	}

	&__list-item-subtext,
	&__list-group-item-subtext {
		@include body-text('sm-supporting');
		margin-top: $gutter-sm;
		padding-left: $padding-sm;
	}

	&__list-group {
		max-height: 200px;
		overflow-y: auto;
		@include scrollbar;
		padding: $padding-sm 0;
	}

	&__list-group-list {
		margin-block-start: 0;
		margin-block-end: 0;

		&:not(:last-child) {
			margin-bottom: $gutter-md;
		}
	}

	&__list-group-title {
		padding-left: $padding-sm;
		@include body-text('regular-emphasis');
	}

	&__list-group-name {
		text-transform: uppercase;
	}

	&__list-group-counter {
		color: $heading-color-sm;
	}

	&__list-group-item {
		margin-inline-start: 0;
		padding-left: $padding-md;
		font-size: $bd-txt-font-size-regular;
	}

	&__list-group-no-results {
		&:not(:first-child) {
			margin-top: $gutter-md;
		}
		&:not(:last-child) {
			margin-bottom: $gutter-md;
		}

		.autosuggest__list-group-item {
			color: $neutral-2;
		}
	}
}

::v-deep .end-results {
	padding: $padding-sm;
	margin-bottom: 0;
}
</style>
